import { useState } from 'react';
import * as Icons from '../icons';
import type { Contact, ExperienceEntry } from '../types';
import { titleCase, dateRange, durationMonths, fmtDuration, totalExperienceMonths, avgTenureMonths } from '../util';

interface Props {
  contact: Contact | null;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  onToggleShortlist?: () => void;
}

type Tab = 'overview' | 'experience' | 'education' | 'skills';

const iconBtnCls =
  'inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-50';

const tabCls = (active: boolean) =>
  'border-0 bg-transparent pt-0 pb-3 text-sm transition-colors ' +
  (active
    ? 'border-b-2 border-[var(--accent)] font-medium text-[var(--text-primary)]'
    : 'border-b-2 border-transparent text-[var(--text-muted)]');

export function ProfileDrawer({ contact, onClose, onPrev, onNext, onToggleShortlist }: Props) {
  const [tab, setTab] = useState<Tab>('overview');
  if (!contact) return null;
  const p = contact.profileData;
  const fullName = titleCase(p.full_name);
  const company = titleCase(p.job_company_name);
  const school = titleCase(p.education?.[0]?.school?.name);
  const states = contact.recruiterStates || [];
  const isShortlisted = states.includes('shortlisted');
  return (
    <>
      <div
        className="fixed inset-0 z-[200] bg-black/30 animate-[fadeIn_0.15s_ease]"
        onClick={onClose}
      />
      <aside
        className="fixed right-0 top-0 z-[210] flex h-screen w-[min(440px,90vw)] flex-col border-l border-[var(--border-default)] bg-[var(--surface)] shadow-xl animate-[slideInRight_0.25s_ease]"
        role="dialog"
        aria-label="Profile"
        style={{ ['--enter' as string]: 'translateX(0)' }}
      >
        <div className="flex items-center justify-between border-b border-[var(--border-default)] px-4 py-3">
          <div className="flex gap-1">
            <button className={iconBtnCls} title="Previous" onClick={onPrev} disabled={!onPrev}>
              <Icons.ChevronLeft />
            </button>
            <button className={iconBtnCls} title="Next" onClick={onNext} disabled={!onNext}>
              <Icons.ChevronRight />
            </button>
          </div>
          <div className="flex gap-1">
            <button className={iconBtnCls} title="Pin">
              <Icons.Pin2 />
            </button>
            <button className={iconBtnCls} title="More">
              <Icons.MoreHoriz />
            </button>
            <button className={iconBtnCls} title="Close" onClick={onClose}>
              <Icons.Close />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-10 pt-5">
          <h2 className="m-0 mb-1.5 flex items-center justify-between text-[22px] font-semibold">
            {fullName}
            {p.linkedin_url ? (
              <span
                className="inline-flex items-center justify-center rounded-md text-white"
                style={{ width: 22, height: 22, background: '#0a66c2' }}
              >
                <Icons.LinkedIn style={{ width: 14, height: 14 }} />
              </span>
            ) : null}
          </h2>
          <div className="mb-2.5 text-sm text-[var(--text-muted)]">{titleCase(p.location_name)}</div>
          <div className="mb-4 flex flex-wrap gap-2">
            {company ? <ChipWithFav letter={company.slice(0, 1)} label={company} /> : null}
            {school ? (
              <ChipWithFav
                letter={school.slice(0, 1)}
                label={school.split(' ').slice(0, 2).join(' ')}
              />
            ) : null}
          </div>

          <div className="mb-6 flex gap-2">
            <button
              className={
                'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors ' +
                (isShortlisted
                  ? 'border-[var(--accent)] bg-[var(--accent-muted)] font-medium text-[var(--accent-strong)] hover:bg-[var(--accent-subtle)]'
                  : 'border-[var(--border-default)] bg-[var(--surface)] hover:bg-[var(--surface-muted)]')
              }
              onClick={() => onToggleShortlist?.()}
              disabled={!onToggleShortlist}
            >
              <Icons.Bookmark style={{ width: 14, height: 14 }} />
              {isShortlisted ? 'Shortlisted' : 'Add to Shortlist'}
            </button>
          </div>

          <div className="mb-5 flex gap-6 border-b border-[var(--border-default)]">
            {(['overview', 'experience', 'education', 'skills'] as Tab[]).map(t => (
              <button key={t} className={tabCls(tab === t)} onClick={() => setTab(t)}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {tab === 'overview' ? (
            <>
              <DrawerRow icon={<Icons.Info />} label="Status">
                <span className="text-[var(--text-muted)]">No status</span>
              </DrawerRow>
              <DrawerRow icon={<Icons.Bookmark />} label="Contact">
                <a className="cursor-pointer text-[var(--text-link)] hover:underline">
                  Email & Phone (1 credit)
                </a>
                <span className="flex-1" />
                <a className="cursor-pointer text-[var(--text-muted)] hover:underline">
                  Add manually
                </a>
              </DrawerRow>
              <DrawerRow icon={<Icons.Diamond />} label="Tags">
                <a className="cursor-pointer text-[var(--text-link)] hover:underline">Add tags</a>
              </DrawerRow>

              <Section
                title="Experience"
                meta={`${fmtDuration(totalExperienceMonths(contact))} total · ${fmtDuration(
                  avgTenureMonths(contact),
                )} average tenure`}
              >
                {(p.experience || []).map((x, i) => <ExperienceCard key={i} x={x} />)}
              </Section>

              <Section title="Education">
                {(p.education || []).map((e, i) => (
                  <EduCard key={i} e={e} />
                ))}
              </Section>
            </>
          ) : null}
          {tab === 'experience' ? (
            <Section title="Experience">
              {(p.experience || []).map((x, i) => <ExperienceCard key={i} x={x} />)}
            </Section>
          ) : null}
          {tab === 'education' ? (
            <Section title="Education">
              {(p.education || []).map((e, i) => <EduCard key={i} e={e} />)}
            </Section>
          ) : null}
          {tab === 'skills' ? (
            <Section title="Skills">
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(p.skills || []).map(s => (
                  <span
                    key={s}
                    className="cursor-default rounded-full border border-[var(--border-default)] bg-[var(--surface)] px-3 py-1 text-[13px]"
                  >
                    {titleCase(s)}
                  </span>
                ))}
              </div>
            </Section>
          ) : null}
        </div>
      </aside>
    </>
  );
}

function ChipWithFav({ letter, label }: { letter: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-sm bg-[var(--surface-tertiary)] px-2 py-1 text-[13px]">
      <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-[var(--surface-tertiary)] text-[9px] font-bold text-[var(--text-muted)]">
        {letter}
      </span>
      {label}
    </span>
  );
}

function DrawerRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 border-b border-[var(--border-muted)] py-2.5 text-sm">
      <span className="flex h-4 w-4 flex-none items-center justify-center text-[var(--text-muted)]">
        {icon}
      </span>
      <span className="w-20 text-[var(--text-muted)]">{label}</span>
      {children}
    </div>
  );
}

function Section({
  title,
  meta,
  children,
}: {
  title: string;
  meta?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-5">
      <h3 className="m-0 mb-1 flex items-center gap-2 text-[15px] font-semibold">
        {title}
        {meta ? <span className="text-[13px] font-normal text-[var(--text-muted)]">· {meta}</span> : null}
      </h3>
      {children}
    </div>
  );
}

function ExperienceCard({ x }: { x: ExperienceEntry }) {
  const months = durationMonths(x.start_date, x.end_date);
  return (
    <div className="flex gap-3 border-b border-[var(--border-muted)] py-3.5">
      <div className="flex h-10 w-10 flex-none items-center justify-center rounded-sm bg-[var(--surface-tertiary)] text-[var(--text-tertiary)]">
        {titleCase(x.company.name).slice(0, 1)}
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold">{titleCase(x.title?.name)}</div>
        <div className="text-sm">{titleCase(x.company?.name)}</div>
        <div className="mt-0.5 text-[13px] text-[var(--text-muted)]">
          {dateRange(x.start_date, x.end_date)} · {fmtDuration(months)}
        </div>
        {x.summary ? <div className="mt-1.5 text-[13px]">{x.summary}</div> : null}
        <a className="mt-1 inline-flex cursor-pointer items-center gap-1 text-[13px] text-[var(--text-link)] hover:underline">
          <Icons.Bolt style={{ width: 12, height: 12 }} />
          Unlock Compensation Estimate
        </a>
      </div>
    </div>
  );
}

function EduCard({ e }: { e: NonNullable<Contact['profileData']['education']>[number] }) {
  return (
    <div className="flex gap-3 border-b border-[var(--border-muted)] py-3.5">
      <div className="flex h-10 w-10 flex-none items-center justify-center rounded-sm bg-[var(--surface-tertiary)] text-[var(--text-tertiary)]">
        {titleCase(e.school?.name).slice(0, 1)}
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold">{titleCase(e.school?.name)}</div>
        {e.degrees?.length ? (
          <div className="text-sm">
            {titleCase(e.degrees[0])}
            {e.majors?.length ? ', ' + titleCase(e.majors[0]) : ''}
          </div>
        ) : null}
        <div className="mt-0.5 text-[13px] text-[var(--text-muted)]">
          {dateRange(e.start_date, e.end_date)}
        </div>
      </div>
    </div>
  );
}
